import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePartnerAuth } from '../contexts/PartnerAuthContext';
import { useBranding, buildBrandingUrl } from '../contexts/BrandingContext';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  ArrowDownToLine,
  History,
  Settings,
  LogOut,
  Handshake,
  Menu,
  X,
} from 'lucide-react';
import { cn } from '../lib/utils';
import ThemeToggle from './ThemeToggle';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/partner' },
  { icon: Users, label: 'Customers', path: '/partner/customers' },
  { icon: CreditCard, label: 'Payment History', path: '/partner/payment-history' },
  { icon: ArrowDownToLine, label: 'Withdrawal Requests', path: '/partner/withdrawals/request' },
  { icon: History, label: 'Withdrawal History', path: '/partner/withdrawals/history' },
  { icon: Settings, label: 'Settings', path: '/partner/settings' },
];

const PartnerLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { partner, logout } = usePartnerAuth();
  const { branding } = useBranding();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const handleLogout = () => {
    logout();
    navigate('/partner/login');
  };

  const logoSrc = buildBrandingUrl(
    branding?.app_logo_mobile || branding?.app_logo || branding?.logo_url
  );

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  const closeMobileNav = () => setMobileNavOpen(false);

  const brandMark = logoSrc ? (
    <img
      src={logoSrc}
      alt={branding?.brand_name || 'Logo'}
      className="h-8 max-w-[100px] object-contain shrink-0"
    />
  ) : (
    <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center shrink-0">
      <Handshake className="w-4 h-4 text-white" />
    </div>
  );

  const sidebar = (
    <>
      <div className="h-16 flex items-center justify-between px-4 border-b border-border gap-2">
        <div className="flex items-center gap-3 min-w-0">
          {brandMark}
          <div className="min-w-0">
            <div className="text-sm font-bold text-foreground truncate">
              {branding?.brand_name || 'Partner Portal'}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {partner?.partner_name || partner?.email}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <div className="hidden md:block">
            <ThemeToggle />
          </div>
          <button
            type="button"
            className="md:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground"
            onClick={closeMobileNav}
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={closeMobileNav}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors text-sm font-semibold',
                isActive
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <item.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-border space-y-2">
        <div className="md:hidden px-1">
          <ThemeToggle className="w-full justify-center" />
        </div>
        <div className="px-3 py-2 mb-1">
          <p className="text-xs font-medium text-foreground truncate">
            {partner?.partner_name}
          </p>
          <p className="text-xs text-muted-foreground truncate">{partner?.email}</p>
        </div>
        <button
          onClick={handleLogout}
          data-testid="partner-logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-destructive/10 transition-colors text-destructive"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-sm font-semibold">Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      <header className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-card border-b border-border flex items-center px-3 gap-2">
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          className="p-2 rounded-lg hover:bg-muted text-foreground shrink-0"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        {brandMark}
        <span className="text-sm font-semibold text-foreground truncate">
          {branding?.brand_name || 'Partner Portal'}
        </span>
      </header>

      {mobileNavOpen && (
        <button
          type="button"
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          aria-label="Close menu overlay"
          onClick={closeMobileNav}
        />
      )}

      <aside
        className={cn(
          'bg-card border-r border-border flex flex-col w-64 shrink-0',
          'fixed md:static inset-y-0 left-0 z-50',
          'transition-transform duration-300 ease-in-out',
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
      >
        {sidebar}
      </aside>

      <main className="flex-1 min-w-0 flex flex-col overflow-hidden pt-14 md:pt-0">
        <div className="flex-1 p-3 sm:p-6 overflow-y-auto overflow-x-hidden">
          {children}
        </div>
      </main>
    </div>
  );
};

export default PartnerLayout;
